// Patient-side client for the pharmacy PUBLIC storefront
// (`/api/pharmacy/public/*` on the shared backend). These endpoints are
// unauthenticated by design: the catalogue is public and orders are
// guest-placed (name + phone), tracked later with orderNumber + phone.
//
// Kept separate from `api.ts`'s `http` because that client is pinned to the
// patient namespace (`/api/patient`) and attaches patient auth headers.

const PATIENT_BASE =
  (import.meta.env?.VITE_PATIENT_API_BASE_URL as string | undefined) || '/api/patient';

/** `http://host:4000/api/patient` → `http://host:4000/api/pharmacy/public` */
const PHARMACY_PUBLIC_BASE = `${PATIENT_BASE.replace(/\/api\/patient\/?$/, '')}/api/pharmacy/public`;

// ─── Types (mirror pharmacyPublicStore.service.ts payloads) ──────────────────

/**
 * What a pharmacy promises about delivery, in its own terms. Every figure is
 * the pharmacy's own setting from its panel — nothing here is a platform
 * default.
 */
export interface PublicDeliveryRules {
  feeInr: number;
  minOrderInr: number;
  etaMinMinutes: number | null;
  etaMaxMinutes: number | null;
  codEnabled: boolean;
  codLimitInr: number;
  prepaidDiscountPct?: number | null;
  servicedPincodes: string[];
  slots?: { label: string; from: string; to: string }[];
}

export interface PublicStoreSummary {
  slug: string;
  name: string;
  /** The pharmacy's 12-digit NeoHealthCard identity; null until one is issued. */
  nh12: string | null;
  city: string | null;
  state: string | null;
  address: string | null;
  pincode: string | null;
  phone: string | null;
  logoUrl: string | null;
  verified: boolean;
  /** Null until somebody actually reviewed the store — never a zero score. */
  rating: number | null;
  reviewCount: number;
  /** The store's own published hours, not a tag someone typed. */
  openingHours: string | null;
  pickup: boolean;
  delivery: boolean;
  deliveryRadiusKm: number | null;
  deliveryRules: PublicDeliveryRules | null;
  /** How much of the catalogue is listed, and how much of that is in stock. */
  catalog: { listed: number; inStock: number } | null;
}

/** A label pharmacies have actually filed listings under (`/public/categories`). */
export interface PublicStoreCategory {
  tag: string;
  count: number;
  /** `CATEGORY` when filed as one; `TAG` when it came from free-form tags. */
  kind: string;
}

/** "30–90 min", or nothing when the pharmacy promised nothing. */
export function deliveryEtaLabel(rules: PublicDeliveryRules | null | undefined): string | null {
  if (!rules) return null;
  const { etaMinMinutes: lo, etaMaxMinutes: hi } = rules;
  if (lo == null && hi == null) return null;
  if (lo != null && hi != null) return `${lo}–${hi} min`;
  return `${lo ?? hi} min`;
}

/** Address, city and pincode as the pharmacy recorded them. */
export function storePlaceLine(s: PublicStoreSummary): string {
  return [s.address, s.city, s.pincode].filter(Boolean).join(', ');
}

export interface PublicStoreMedicine {
  id: string;
  medicineName: string;
  genericName: string | null;
  brand: string | null;
  strength: string | null;
  dosageForm: string | null;
  sellingPrice: number;
  mrp: number;
  prescriptionRequired: boolean;
  inStock: boolean;
}

export interface PublicStoreDetail {
  store: PublicStoreSummary;
  medicines: PublicStoreMedicine[];
  notBuilt?: string[];
}

export interface PublicOrderInput {
  buyerName: string;
  buyerPhone: string;
  buyerNhcId?: string;
  items: { medicineId: string; quantity: number }[];
  fulfillmentType: 'PICKUP' | 'DELIVERY';
  deliveryAddress?: string;
  prescriptionId?: string;
}

export interface PublicOrderResult {
  orderNumber: string;
  totalAmount: number;
  prescriptionRequired: boolean;
  fulfillmentType: 'PICKUP' | 'DELIVERY';
  trackWith: { orderNumber: string; phoneHint: string };
}

export interface PublicOrderTimelineEntry {
  status: string;
  note: string | null;
  at: string;
}

export interface PublicTrackedOrder {
  orderNumber: string;
  status: string;
  paymentStatus: string;
  fulfillmentType: string;
  placedAt: string | null;
  pharmacy: { name: string; phone: string | null; city: string | null } | null;
  items: { medicineName: string; strength: string | null; quantity: number; prescriptionRequired: boolean; lineTotal: number }[];
  totalAmount: number;
  timeline: PublicOrderTimelineEntry[];
  prescriptionRequired: boolean;
}

export class PharmacyPublicApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public fieldErrors?: { field: string; message: string }[],
  ) {
    super(message);
    this.name = 'PharmacyPublicApiError';
  }
}

// ─── Fetch helper (same `{success,message,data}` envelope as the backend) ────

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PHARMACY_PUBLIC_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON error body */
  }
  const envelope = (json ?? {}) as { success?: boolean; message?: string; data?: T; errors?: { field: string; message: string }[] };
  if (!res.ok || envelope.success === false) {
    throw new PharmacyPublicApiError(
      res.status,
      envelope.message || `Request failed (${res.status})`,
      envelope.errors,
    );
  }
  return (envelope.data ?? (json as T)) as T;
}

// ─── API surface ─────────────────────────────────────────────────────────────

// ─── Placed-order memory (tracking credentials) ──────────────────────────────
// Public orders are proven by orderNumber + phone; this device-local list is
// what lets the Orders page hydrate live statuses for orders placed here.

export interface RememberedPharmacyOrder {
  orderNumber: string;
  phone: string;
  slug: string;
  pharmacyName: string;
  placedAt: string;
}

const PLACED_ORDERS_KEY = 'nhc_patient_pharmacy_public_orders';

export function rememberPharmacyOrder(entry: RememberedPharmacyOrder): void {
  try {
    const list = listRememberedPharmacyOrders().filter((o) => o.orderNumber !== entry.orderNumber);
    list.unshift(entry);
    localStorage.setItem(PLACED_ORDERS_KEY, JSON.stringify(list.slice(0, 50)));
  } catch {
    /* storage unavailable — tracking list is best-effort */
  }
}

export function listRememberedPharmacyOrders(): RememberedPharmacyOrder[] {
  try {
    const raw = localStorage.getItem(PLACED_ORDERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const pharmacyPublicApi = {
  /** Pharmacies with the online store enabled. */
  listStores: () => request<PublicStoreSummary[]>('/stores'),

  /**
   * The labels pharmacies have filed their listings under, with how many
   * listings each covers. Supporting detail: a network where nobody
   * categorised anything simply has none.
   */
  listCategories: () => request<PublicStoreCategory[]>('/categories'),

  /** One store + its REAL medicine catalogue (prices/stock from Medicine Master). */
  getStore: (slug: string) => request<PublicStoreDetail>(`/stores/${encodeURIComponent(slug)}`),

  /** Place a real order → PharmacySaleOrder in the pharmacy panel's queue. */
  placeOrder: (slug: string, body: PublicOrderInput) =>
    request<PublicOrderResult>(`/stores/${encodeURIComponent(slug)}/order`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Track a placed order — proven by the phone number used to place it. */
  trackOrder: (orderNumber: string, phone: string) =>
    request<PublicTrackedOrder>(`/orders/${encodeURIComponent(orderNumber)}?phone=${encodeURIComponent(phone)}`),
};
